import { useMemo, useSyncExternalStore } from 'react';
// Paleta oficial do Tailwind v3 — é EXATAMENTE o módulo que tailwind.config.js
// usa pra gerar os tokens semânticos do app (success=emerald, warning=amber,
// danger=red, info=blue). Importar daqui garante hex idêntico ao das classes
// utilitárias, sem copiar valores à mão. Browser-safe: o único require
// transitivo (picocolors, via util/log) tem `browser` field no package.json.
import twColors from 'tailwindcss/colors';

const { amber, blue, emerald, red } = twColors;

// ─────────────────────────────────────────────────────────────────────────────
// FONTE ÚNICA DE COR (convergência com o design system — plano UX 04)
//
// Antes este arquivo definia uma paleta própria (hex/oklch soltos). Agora cada
// token deriva da MESMA fonte que o resto do app:
//
//   • Neutros (bg/line/ink/muted/…) → escala `--mono-*` do tema ativo
//     (definida por tema em src/index.css e consumida pelo Tailwind como
//     `mono-*` em tailwind.config.js). Mudou a escala lá, reflete aqui.
//   • Semânticos (warn/ok/danger/accent) → warning/success/danger/info do
//     tailwind.config.js (amber/emerald/red/blue de `tailwindcss/colors`).
//
// Por que resolvemos as vars com getComputedStyle em vez de emitir strings
// `rgb(var(--mono-N))`: os tokens alimentam também ATRIBUTOS SVG (ícones
// `color={T.x}` via HugeiconsIcon, `stroke` do recharts), e presentation
// attributes de SVG NÃO suportam `var()` (limitação da spec de custom
// properties). Valores concretos funcionam em qualquer contexto.
// ─────────────────────────────────────────────────────────────────────────────

const SHARED = {
    sidebar: 255,
    // Alturas PADRÃO da página de Operações:
    //   - Toolbar topo: headerH = 50px
    //   - Footer da DataTable e do DetailPanel: footerH = 50px
    //   - Linha da tabela: rowH = 40px
    // Padronizadas pra que Split vertical / Drawer alinhem visualmente entre
    // tabela e painel de detalhe.
    headerH: 50,
    footerH: 50,
    rowH: 40,
    filterH: 46,
    sans: 'Inter, -apple-system, system-ui, sans-serif',
    mono: '"JetBrains Mono", ui-monospace, SFMono-Regular, monospace',
};

// Fallback = escala do tema claro (:root em src/index.css). Usado apenas
// quando a var não resolve (ex: jsdom em teste); em runtime real as vars
// existem em todos os temas.
const MONO_FALLBACK = {
    50: '250 250 250',
    100: '245 245 245',
    200: '229 229 229',
    300: '212 212 212',
    400: '163 163 163',
    500: '115 115 115',
    600: '82 82 82',
    700: '64 64 64',
    800: '38 38 38',
    900: '23 23 23',
    950: '10 10 10',
};

// Cache por tema: as vars `--mono-*` são estáticas por `data-theme`, então
// basta ler o getComputedStyle uma vez por tema visitado.
const monoLadderCache = new Map();

function readMonoLadder(themeId) {
    if (monoLadderCache.has(themeId)) {
        return monoLadderCache.get(themeId);
    }
    const styles =
        typeof window !== 'undefined' && typeof document !== 'undefined'
            ? window.getComputedStyle(document.documentElement)
            : null;
    const ladder = {};
    for (const step of Object.keys(MONO_FALLBACK)) {
        const raw = styles ? styles.getPropertyValue(`--mono-${step}`).trim() : '';
        ladder[step] = `rgb(${raw || MONO_FALLBACK[step]})`;
    }
    monoLadderCache.set(themeId, ladder);
    return ladder;
}

// ── Assinatura do tema ativo via atributo `data-theme` do <html> ────────────
// O ThemeProvider aplica o atributo num useEffect (DEPOIS do render dos
// consumidores). Ler o ATRIBUTO — e não o estado do contexto — garante que a
// escolha do dicionário e os valores das vars andem sempre juntos; o
// MutationObserver re-renderiza os consumidores no instante em que o atributo
// muda (troca de tema ou primeira aplicação no boot).
const themeAttrListeners = new Set();
let themeAttrObserver = null;

function subscribeThemeAttr(listener) {
    themeAttrListeners.add(listener);
    if (!themeAttrObserver && typeof MutationObserver !== 'undefined' && typeof document !== 'undefined') {
        themeAttrObserver = new MutationObserver(() => {
            for (const notify of themeAttrListeners) {
                notify();
            }
        });
        themeAttrObserver.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['data-theme'],
        });
    }
    return () => {
        // Observer fica vivo (é único e barato) — evita corrida create/destroy
        // entre os ~30 componentes que usam useTokens simultaneamente.
        themeAttrListeners.delete(listener);
    };
}

function getThemeAttr() {
    if (typeof document === 'undefined') {
        return 'claro';
    }
    // Antes da primeira aplicação do ThemeProvider o atributo não existe e as
    // vars resolvem pros defaults do :root (== tema claro) — o fallback 'claro'
    // mantém dicionário e vars consistentes nesse primeiro frame.
    return document.documentElement.getAttribute('data-theme') || 'claro';
}

/**
 * Monta o dicionário de tokens do tema. Cada valor à direita indica o token de
 * origem no design system; o comentário registra o hex/oklch antigo (paleta
 * própria pré-convergência) pra auditoria visual.
 */
function buildTokens(themeId) {
    const m = readMonoLadder(themeId);

    const LIGHT = {
        ...SHARED,
        // Branco puro proposital: é a mesma superfície `bg-white` que o DS usa
        // em Card/Modal/Toast no tema claro (o topo da escala mono-50 não é
        // branco). Único neutro que não vem da escala.
        bg: '#ffffff',
        bgSoft: m[50], //   era #f8f8f6
        bgMid: m[100], //   era #f0efeb
        line: m[200], //    era #e4e4e0
        lineMid: m[300], // era #ccccc8
        ink: m[900], //     era #111111
        inkMid: m[700], //  era #3a3a3a
        muted: m[500], //   era #88887f
        subtle: m[300], //  era #c0bfb8
        accent: blue[600], //     info      — era oklch(51% 0.155 252)
        accentHov: blue[700], //  info-700  — era oklch(46% 0.155 252)
        accentBg: blue[50], //    info-50   — era oklch(96.5% 0.022 252)
        accentLine: blue[200], // info-200  — era oklch(88% 0.06 252)
        warn: amber[700], //      warning.text — era oklch(58% 0.16 30); 700 (não
        //                        600) preserva o L≈58% antigo e o contraste AA
        //                        do texto branco no badge do carrinho.
        warnBg: amber[50], //     warning-50   — era oklch(97% 0.022 30)
        warnLine: amber[200], //  warning-200  — era oklch(88% 0.07 30)
        ok: emerald[700], //      success.text — era oklch(50% 0.15 148)
        okBg: emerald[100], //    success.soft — era oklch(96% 0.04 148)
        danger: red[600], //      danger       — era #c00
        dangerBg: red[50], //     danger-50    — era #fff5f5
        dangerLine: red[200], //  danger.border — era #fcc
    };

    const DARK = {
        ...SHARED,
        // Um degrau abaixo do mono-950 do tema escuro — mantém a tabela mais
        // funda que os painéis (bgSoft). Único neutro que não vem da escala.
        bg: '#0e0e0f',
        bgSoft: m[950], //  era #161617
        bgMid: m[900], //   era #1d1d1f
        line: m[800], //    era #2a2a2c
        lineMid: m[700], // era #3a3a3c
        ink: m[50], //      era #f5f5f5
        inkMid: m[200], //  era #cccccc
        muted: m[400], //   era #888880
        subtle: m[600], //  era #5a5a5c
        accent: blue[400], //     info-400  — era oklch(72% 0.155 252)
        accentHov: blue[300], //  info-300  — era oklch(78% 0.155 252)
        accentBg: blue[950], //   info-950  — era oklch(28% 0.07 252)
        accentLine: blue[900], // info-900  — era oklch(38% 0.07 252)
        warn: amber[500], //      warning-500  — era oklch(76% 0.18 50)
        warnBg: amber[950], //    warning-950  — era oklch(28% 0.05 50)
        warnLine: amber[900], //  warning-900  — era oklch(38% 0.06 50)
        ok: emerald[500], //      success-500  — era oklch(70% 0.16 148)
        okBg: emerald[950], //    success-950  — era oklch(26% 0.06 148)
        danger: red[400], //      danger-400   — era #ff7878
        dangerBg: red[950], //    danger-950   — era oklch(28% 0.06 25)
        dangerLine: red[900], //  danger-900   — era oklch(38% 0.07 25)
    };

    // As diferenças de tom (preto mais fundo, azulado do gelo) já vivem na
    // escala --mono-* de cada tema — os dicionários só deslocam os degraus.
    const DEEP_DARK = {
        ...DARK,
        bg: '#000000', // preto puro proposital (identidade do tema)
    };

    const ICE = {
        ...LIGHT,
        bg: m[50], //    era #f7fbff (≈ mono-50 do branco-gelo)
        bgSoft: m[100], // era #eef5fb
        bgMid: m[200], //  era #e2ecf5
        // Um degrau abaixo do claro: 200 colidiria com bgMid (chips `soft`
        // usam fundo bgMid + borda line — precisam continuar distintos).
        line: m[300], //   era #d4e0eb
    };

    const byTheme = {
        claro: LIGHT,
        'branco-gelo': ICE,
        escuro: DARK,
        'escuro-profundo': DEEP_DARK,
    };
    return byTheme[themeId] || LIGHT;
}

/**
 * Hook que retorna o objeto de tokens correspondente ao tema ativo.
 * Mantém o mesmo shape do antigo `T` global; consumir como `const T = useTokens();`.
 */
export function useTokens() {
    const themeId = useSyncExternalStore(subscribeThemeAttr, getThemeAttr);
    return useMemo(() => buildTokens(themeId), [themeId]);
}
